import React, { useCallback, useEffect, useRef } from 'react';
import {
  LiteYouTubeElement,
  YouTubePlayer,
  YouTubePlayerProps,
  type YouTubeIframePlayer,
} from '../YouTubePlayer/YouTubePlayer';
import AspectRatio from '@jbhi-fi/lanyard-ui/components/AspectRatio';
import { styles } from './StackYouTubePlayer.css';
import { useStackVideoPlayer } from '../hooks/useStackVideoPlayer';

export type StackYouTubeVariant = 'default' | 'genericPage';

export type StackYouTubePlayerProps = YouTubePlayerProps & {
  variant?: StackYouTubeVariant;
  isExternallyControllable?: boolean;
};

export const StackYouTubePlayer = ({
  variant = 'default',
  isExternallyControllable = false,
  ...youTubePlayerProps
}: StackYouTubePlayerProps) => {
  const stackVideoPlayer = useStackVideoPlayer();
  const containerRef = useRef<HTMLDivElement>(null);

  const getPlayer = useCallback(async (): Promise<YouTubeIframePlayer | null> => {
    const liteYouTubeElement = containerRef.current?.querySelector<LiteYouTubeElement>('lite-youtube');
    if (!liteYouTubeElement) return null;

    try {
      return await liteYouTubeElement.getYTPlayer();
    } catch {
      return null;
    }
  }, []);

  const sendCommand = useCallback(
    async (command: (player: YouTubeIframePlayer) => void) => {
      const player = await getPlayer();
      if (!player) return;

      command(player);
    },
    [getPlayer]
  );

  const seek = useCallback(
    (time: number) => {
      void sendCommand(player => player.seekTo(time, true));
    },
    [sendCommand]
  );

  const play = useCallback(() => {
    void sendCommand(player => player.playVideo());
  }, [sendCommand]);

  const pause = useCallback(() => {
    void sendCommand(player => player.pauseVideo());
  }, [sendCommand]);

  useEffect(() => {
    if (!stackVideoPlayer || !isExternallyControllable) return;

    const controlledElement = containerRef.current;
    const player = { seek, play, pause };

    stackVideoPlayer.setControlledPlayer(player);
    stackVideoPlayer.setControlledPlayerElement(controlledElement);

    return () => {
      if (stackVideoPlayer.controlledPlayerRef.current === player) {
        stackVideoPlayer.setControlledPlayer(null);
      }
      if (stackVideoPlayer.controlledPlayerElementRef.current === controlledElement) {
        stackVideoPlayer.setControlledPlayerElement(null);
      }
    };
  }, [isExternallyControllable, pause, play, seek, stackVideoPlayer]);

  return (
    <div className={styles.stackYouTubePlayer({ variant })} ref={containerRef}>
      <AspectRatio ratio={16 / 9}>
        <YouTubePlayer {...youTubePlayerProps} />
      </AspectRatio>
    </div>
  );
};
